'use client';

import { Button, DeprecatedLinkButton as LinkButton, Text } from '@lmn/design-system';
import {
  type HTMLAttributes,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { css, useTheme } from 'styled-components';

import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { TextBase } from '@/app/components/hotel-detail/common/text';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { type GoodToKnow, type RatingByType, useMetaReview } from '@/app/components/hotel-detail/hooks/useMetaReview';
import {
  decorateRatingScore,
  getColor,
  getInterval,
  trackReviewUserInteraction,
  type UserInteraction,
} from '@/app/components/hotel-detail/trust-you/trustyou.helper';
import TrustYouReviewsModalContent, {
  type TrustYouReviewsModalContentProps,
} from '@/app/components/hotel-detail/trust-you/TrustYouReviewsModalContent';
import TrustYouScore from '@/app/components/hotel-detail/trust-you/TrustYouScore';
import BasicModalHeader from '@/app/components/search/components/Modal/LayoutElements/BasicModalHeader';
import OpenXModal from '@/app/components/search/components/Modal/OpenXModal';
import { useTranslation } from '@/app/components/translations/Translation';
import { formatNumber } from '@/utils/numberUtils.ts';
import { IconUsers } from '@/app/common/icons/libraryIcons';
import type { Brand } from '@/domainLogic/index.ts';
import { Icon } from '@/app/common/icons/Icon';
import { useIsMobile } from '../hooks/useMediaSSR';

const createTravelTypeIcon = (): Dynamic<ReactElement> => ({
  all: <IconUsers />,
  family: (
    <Icon category="People and users" name="icon-family" size={25} footprint={20} color="iconPrimary" alt="families" />
  ),
  couple: <Icon category="People and users" name="icon-couple" size={20} color="iconPrimary" alt="couples" />,
  business: <Icon category="Services" name="icon-briefcase" size={20} color="iconPrimary" alt="business" />,
  solo: <Icon category="People and users" name="icon-user" size={23} footprint={20} color="iconPrimary" alt="solo" />,
});

// #region Styles
const TrustYouContainer = styled.div`
  background-color: ${({ theme }) => theme.commons.colors.gray0};
  padding: 16px;
  border-radius: 8px;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: repeat(4, auto);
  grid-template-areas:
    'summary'
    'categories'
    'good-to-know'
    'bottom';

  @media ${customCss.breakpoints.sm} {
    padding: 24px;
    grid-template-columns: 2fr 1fr;
    grid-template-rows: repeat(3, auto);
    grid-template-areas:
      'summary summary'
      'categories type-ratings'
      'categories good-to-know'
      'bottom bottom';
    gap: 16px 64px;
  }
`;

const Score = styled.div`
  grid-area: summary;
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;

  @media ${customCss.breakpoints.sm} {
    margin-bottom: 24px;
  }
`;

const Categories = styled.div`
  grid-area: categories;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 24px;
  padding: 8px 0;
  @media ${customCss.breakpoints.sm} {
    gap: 16px 56px;
    grid-template-columns: 1fr 1fr;
    padding: 0;
  }
`;

const Category = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const Name = styled.div`
  position: relative;
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const ProgressBar = styled.div<{ $score: number; $color: string }>`
  background-color: ${({ theme }) => theme.commons.colors.gray100};
  border-radius: 3px;
  height: 4px;
  margin: 4px 0 2px;
  position: relative;
  width: 100%;

  &::after {
    background-color: ${({ $color }) => $color};
    border-radius: 3px;
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: ${({ $score }) => $score}%;
  }
`;

const BottomContainer = styled.div`
  grid-area: bottom;
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const TrustYouBadge = styled.img`
  height: 20px;
  width: 110px;
  margin-left: auto;
  align-self: flex-start;
`;

const CategoryScore = styled.span`
  ${(p) => p.theme.kallisto.mixins.font('Title5')};
  @media ${customCss.breakpoints.sm} {
    ${(p) => p.theme.kallisto.mixins.font('Title4')};
  }
`;

const CategoryName = styled.span`
  ${(p) => p.theme.kallisto.mixins.font('BodySmall')};
  @media ${customCss.breakpoints.sm} {
    ${(p) => p.theme.kallisto.mixins.font('Body')};
  }
`;

const Quotes = styled.div`
  display: none;
  @media ${customCss.breakpoints.sm} {
    display: block;
  }
`;

const Quote = styled(Text).attrs({ variant: 'BodySmall', color: 'textSecondary' })`
  &::before {
    content: '«';
    margin-right: 2px;
  }
  &::after {
    content: '»';
    margin-left: 2px;
  }
`;

const TypeRatingContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const TypeRatingBadge = styled.div`
  font-size: ${({ theme }) => theme.commons.font.sizes.base};
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.commons.colors.gray50};
  color: ${({ theme }) => theme.commons.colors.gray700};
  cursor: pointer;
`;

const TypeScore = styled.span`
  color: ${({ theme }) => theme.commons.colors.gray400};
  margin-left: -2px;

  &::before {
    content: '·';
    margin-right: 4px;
  }
`;

const GoodToKnowsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const GoodToKnowBadge = styled.div<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background-color: ${({ $color, theme }) => $color || theme.commons.colors.gray50};
  border-radius: 8px;
  color: ${({ theme }) => theme.commons.colors.gray800};
  padding: 8px 12px;

  img {
    width: 14px;
    height: 14px;
  }
`;

const SectionTitle = styled(Text).attrs({ variant: 'Title6' })`
  width: 100%;
  margin-bottom: 16px;
`;

const TypeRatingsSection = styled.div`
  display: none;
  @media ${customCss.breakpoints.xl} {
    display: block;
    grid-area: type-ratings;
  }
`;

// #endregion Styles

type TrustYouReviewsSummaryProps = {
  hotelName: string;
  hotelOpinion: HotelOpinion;
  language: string;
  showMoreInfoEnabled: boolean;
  reviewsSectionTitleId: string;
  /** Render the full reviews content inline (Evo details modal) instead of a summary card + "view more" modal. */
  inline?: boolean;
} & HTMLAttributes<HTMLElement>;

type TravelerType = 'all' | 'family' | 'couple' | 'business' | 'solo';

type GoodToKnowItem = {
  icon: ReactElement | null;
  color: string;
  text: string;
};

type TypeRatingItem = {
  icon: ReactElement;
  percentage: string;
  type: TravelerType;
  text: string;
};

const trackTypes: Dynamic<UserInteraction> = {
  ShowMoreClick: {
    action: 'see_all_reviews',
    label: 'click',
    category: 'reviews',
  },
  TravelerTypeClick: {
    action: 'tabs',
    label: 'all',
    category: 'reviews',
  },
};

type TrackType = keyof typeof trackTypes;

function trackUserInteraction(type: TrackType, label?: string) {
  trackReviewUserInteraction(trackTypes, type, label);
}

export default function TrustYouReviewsSection(props: TrustYouReviewsSummaryProps) {
  const { hotelName, hotelOpinion, language, showMoreInfoEnabled, reviewsSectionTitleId, inline = false } = props;
  const [isShowingMoreInfo, setIsShowingMoreInfo] = useState<boolean | undefined>();
  const [selectedTravelerType, setSelectedTravelerType] = useState<TravelerType>('all');
  const { staticData, commonData, localization } = usePageWrapperData();
  const isMobile = useIsMobile(commonData.isMobileDevice);
  const [selectedType, setSelectedType] = useState<TrustYouReviewsModalContentProps['preselectedType'] | undefined>(
    undefined
  );

  const { metaReview, isLoading, isError } = useMetaReview(hotelOpinion.opinionId, language);
  const { t } = useTranslation();
  const { sendCuriosityClickEvent } = useCuriosity();
  const theme = useTheme();

  const { locale } = localization;
  const viewMoreHandler = (e: ReactMouseEvent) => {
    e.preventDefault();
    openMoreInfo();
  };

  const hasAutoOpenedRef = useRef(false);

  const openMoreInfo = useCallback(() => {
    setSelectedType(undefined);
    setIsShowingMoreInfo(true);
    sendCuriosityClickEvent({
      event_name: 'click_reviews_link',
      event_context: 'review_panel',
    });
    trackUserInteraction('ShowMoreClick');
  }, [sendCuriosityClickEvent]);

  useEffect(() => {
    if (!isLoading && metaReview && !hasAutoOpenedRef.current && window.location.hash === '#reviews') {
      hasAutoOpenedRef.current = true;
      openMoreInfo();
    }
  }, [isLoading, metaReview, openMoreInfo]);

  const closeMoreInfo = () => {
    setIsShowingMoreInfo(false);
  };

  const tabClickHandler = useCallback(
    (event: ReactMouseEvent): void => {
      const tab: HTMLElement | null = (event.target as HTMLElement).closest('[data-id]');
      if (tab?.dataset.id) {
        const newType = tab.dataset.id as TravelerType;
        setSelectedTravelerType(newType);
        setSelectedType(newType);
        setIsShowingMoreInfo(true);
      }
    },
    [setSelectedTravelerType]
  );

  // Type ratings functionality
  const typeRatingList = useMemo(
    (): TypeRatingItem[] =>
      metaReview
        ? [
            ...metaReview.ratingByTypeList.map((rating: RatingByType): TypeRatingItem => ({
              icon: createTravelTypeIcon()[rating.type],
              percentage: `${rating.percentage}%`,
              type: rating.type as TravelerType,
              text: t(`vacation.common.rating.type.${rating.type}`),
            })),
          ]
        : [],
    [metaReview, t]
  );

  useEffect((): void => {
    if (!typeRatingList.some((typeRating: TypeRatingItem): boolean => typeRating.type === selectedTravelerType)) {
      setSelectedTravelerType('all');
    }
  }, [typeRatingList, selectedTravelerType]);

  const goodToKnows = useMemo((): GoodToKnowItem[] => {
    const list = metaReview?.goodToKnowList || [];

    return list.map((goodToKnow: GoodToKnow): GoodToKnowItem =>
      goodToKnow.sentiment === 'pos'
        ? {
            icon: (
              <Icon category="Rating and feedback" name="icon-thumb-up" size={14} color="iconPrimary" alt="Like icon" />
            ),
            color: theme.commons.colors.success50,
            text: goodToKnow.text,
          }
        : goodToKnow.sentiment === 'neg'
          ? {
              icon: (
                <Icon
                  category="Rating and feedback"
                  name="icon-thumb-down"
                  size={14}
                  color="iconPrimary"
                  alt="Dislike icon"
                />
              ),
              color: theme.commons.colors.warning50,
              text: goodToKnow.text,
            }
          : {
              icon: null,
              color: '',
              text: goodToKnow.text,
            }
    );
  }, [metaReview, theme]);

  // Evo details modal: reviews render inline (full header + filters + categories, no "view more"
  // modal). Mirrors TripAdvisorReviewsSection; TrustYouReviewsModalContent handles its own
  // loading spinner and error fallback, so short-circuit before the null guard below.
  if (inline) {
    return <TrustYouReviewsModalContent trustYouHotelId={hotelOpinion.opinionId} language={language} inline={true} />;
  }

  if (!metaReview || isError) return null;

  const { categories } = metaReview;

  const reviewCount = formatNumber({ locale, number: metaReview.reviewCount || hotelOpinion.numberOfReviews || 0 });
  const score = metaReview.score || hotelOpinion.rating;

  const ratingInterval = score ? getInterval(score) : undefined;
  const title = isMobile
    ? t('vacation.select.navigation.reviews')
    : t('vacation.select.reviews.hotel', {
        hotelName,
      });

  return isLoading ? undefined : (
    <>
      <TrustYouContainer>
        <Score>
          {score ? (
            <TrustYouScore
              score={score}
              css={`
                font-size: 20px;
                line-height: 28px;
                padding: 8px;
                width: 44px;
                height: 44px;
              `}
            />
          ) : null}
          <div
            css={`
              display: flex;
              flex-direction: column;
            `}
          >
            <Text variant="Body" weight="bold">
              {ratingInterval && t(ratingInterval.translationKey)}
            </Text>
            <Text
              variant="Caption"
              css={css`
                color: ${(p) => p.theme.commons.colors.gray400};
              `}
            >
              {t('vacation.select.details.review.scoredetails', { reviewCount })}
            </Text>
          </div>
          <TrustYouBadge
            srcSet="https://api.trustyou.com/static/img/verified-reviews-small.png 1x, https://api.trustyou.com/static/img/verified-reviews-large.png 2x"
            alt="Verified Reviews TrustYou"
          />
        </Score>

        <TypeRatingsSection>
          {typeRatingList.length > 1 && (
            <>
              <SectionTitle>{t('vacation.select.details.review.typerating')}</SectionTitle>
              <TypeRatingContainer onClick={tabClickHandler}>
                {typeRatingList.map((rating) => (
                  <TypeRatingBadge key={rating.type} data-id={rating.type}>
                    {rating.icon} {rating.text}
                    <TypeScore>{rating.percentage}</TypeScore>
                  </TypeRatingBadge>
                ))}
              </TypeRatingContainer>
            </>
          )}
        </TypeRatingsSection>

        {/* Good To Knows Section */}
        <div
          css={`
            grid-area: good-to-know;
          `}
        >
          {goodToKnows.length > 0 && (
            <>
              <SectionTitle>{t('vacation.select.details.review.goodtoknow')}</SectionTitle>
              <GoodToKnowsContainer>
                {goodToKnows.map((goodToKnow) => (
                  <GoodToKnowBadge key={goodToKnow.text} $color={goodToKnow.color}>
                    {goodToKnow.icon}
                    <TextBase>{goodToKnow.text}</TextBase>
                  </GoodToKnowBadge>
                ))}
              </GoodToKnowsContainer>
            </>
          )}
        </div>

        <Categories>
          {categories.map((category) => (
            <Category key={category.name} title={category.text}>
              <Name>
                <CategoryScore>{decorateRatingScore(category.score)}</CategoryScore>
                <CategoryName>{category.name}</CategoryName>
              </Name>
              <ProgressBar $score={category.score} $color={getColor(category.score)} />
              <Quotes>
                {category.summarySentenceList.map((summarySentence: string) => (
                  <Quote key={summarySentence}>{summarySentence}</Quote>
                ))}
              </Quotes>
            </Category>
          ))}
        </Categories>
        <BottomContainer>
          {showMoreInfoEnabled ? (
            isMobile ? (
              <Button
                variant="primary"
                appearance="outlined"
                onClick={viewMoreHandler}
                aria-labelledby={`view-more-reviews-text ${reviewsSectionTitleId}`}
                css={`
                  width: 100%;
                `}
              >
                <span id="view-more-reviews-text">{t('vacation.select.details.review.viewmore')}</span>
              </Button>
            ) : (
              <LinkButton
                variant="accent"
                onClick={viewMoreHandler}
                aria-labelledby={`view-more-reviews-text ${reviewsSectionTitleId}`}
                css={css`
                  ${(p) => p.theme.kallisto.mixins.font('BodySmall')};
                `}
              >
                <span id="view-more-reviews-text">{t('vacation.select.details.review.viewmore')}</span>
              </LinkButton>
            )
          ) : undefined}
        </BottomContainer>
      </TrustYouContainer>

      <OpenXModal
        isOpen={isShowingMoreInfo ?? false}
        isMobile={isMobile ?? true}
        brand={staticData.domainValues?.brand as Brand}
        onClose={closeMoreInfo}
        onCloseByBrowserHistory={closeMoreInfo}
        onOpenByBrowserHistory={openMoreInfo}
        fullScreen={!isMobile}
        className={isMobile ? 'hotel-reviews-modal' : 'hotel-reviews-modal hotel-modal-lateral'}
        id="hotel-reviews-section-modal"
        aria-label={title}
        bodyBackgroundColor="backgroundPage"
        header={{
          sticky: true,
          element: <BasicModalHeader handleClose={closeMoreInfo}>{title}</BasicModalHeader>,
        }}
      >
        <TrustYouReviewsModalContent
          trustYouHotelId={hotelOpinion.opinionId}
          language={language}
          preselectedType={selectedType || selectedTravelerType}
        />
      </OpenXModal>
    </>
  );
}
